import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import UserMenuButton from "@/components/navigation/UserMenuButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { format, parseISO, startOfDay, subDays } from "date-fns";
import { de } from "date-fns/locale";
import { 
  Clock, Check, LogOut, Loader2, CalendarDays, ChevronDown, Menu,
  Users, CheckCircle, XCircle, MapPin, Mail, Phone
} from "lucide-react";
import axios from "axios";
import AppFooter from "@/components/layout/AppFooter";
import AppHeader from "@/components/navigation/AppHeader";
import { canManagePlan, isPlanAdminUser } from "@/lib/roles";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const MEMBER_SELECTED_PLAN_KEY = "member_selected_plan_id";
const DEFAULT_MEMBER_PLAN_SETTINGS = {
  user_can_see_mydetails_mail: false,
  user_can_see_mydetails_phone: false,
  user_can_see_other_users: false,
  user_can_see_users_needed: false,
  user_can_comment_days: false,
  user_see_schedule_comment: false,
  schedule_comment: "",
};
const MemberDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [dienstplaene, setDienstplaene] = useState([]);
  const [dienstplan, setDienstplan] = useState(null);
  const [planSettings, setPlanSettings] = useState(DEFAULT_MEMBER_PLAN_SETTINGS);
  const [shifts, setShifts] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [savingCommentId, setSavingCommentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switchingPlan, setSwitchingPlan] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [assignmentToCancel, setAssignmentToCancel] = useState(null);
  const [cancelingAssignmentId, setCancelingAssignmentId] = useState(null);

  const loadPlanSettings = useCallback(async (planId) => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/dienstplan/${planId}`);
      setPlanSettings({
        user_can_see_mydetails_mail: Boolean(response.data?.user_can_see_mydetails_mail),
        user_can_see_mydetails_phone: Boolean(response.data?.user_can_see_mydetails_phone),
        user_can_see_other_users: Boolean(response.data?.user_can_see_other_users),
        user_can_see_users_needed: Boolean(response.data?.user_can_see_users_needed),
        user_can_comment_days: Boolean(response.data?.user_can_comment_days),
        user_see_schedule_comment: Boolean(response.data?.user_see_schedule_comment),
        schedule_comment: String(response.data?.schedule_comment || "").trim(),
      });
    } catch (error) {
      console.error("Error fetching dienstplan settings for member:", error);
      setPlanSettings(DEFAULT_MEMBER_PLAN_SETTINGS);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dpRes, assignRes] = await Promise.all([
        axios.get(`${API_URL}/api/dienstplan`),
        axios.get(`${API_URL}/api/my-assignments`)
      ]);

      const plans = dpRes.data || [];
      setDienstplaene(plans);
      setMyAssignments(assignRes.data || []);
      setCommentDrafts({});

      if (plans.length > 0) {
        const savedPlanId = localStorage.getItem(MEMBER_SELECTED_PLAN_KEY);
        const initialPlan =
          plans.find((plan) => String(plan.id) === String(savedPlanId)) || plans[0];
        setDienstplan(initialPlan);
        localStorage.setItem(MEMBER_SELECTED_PLAN_KEY, String(initialPlan.id));

        const [shiftsRes] = await Promise.all([
          axios.get(`${API_URL}/api/shifts/${initialPlan.id}`),
          loadPlanSettings(initialPlan.id),
        ]);
        setShifts(shiftsRes.data);
      } else {
        setDienstplan(null);
        setShifts([]);
        setPlanSettings(DEFAULT_MEMBER_PLAN_SETTINGS);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, [loadPlanSettings]);

  useEffect(() => {
    if (user && isPlanAdminUser(user)) {
      navigate("/dienstplan", { replace: true });
      return;
    }
    fetchData();
  }, [navigate, user, fetchData]);

  const handleConfirmShift = async (shiftId) => {
    try {
      await axios.post(`${API_URL}/api/assignments`, { shift_id: shiftId });
      toast.success("Zusage erfolgreich!");
      
      // Refresh data
      const [assignRes, shiftsRes] = await Promise.all([
        axios.get(`${API_URL}/api/my-assignments`),
        axios.get(`${API_URL}/api/shifts/${dienstplan.id}`)
      ]);
      setMyAssignments(assignRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      console.error("Error confirming shift:", error);
      toast.error(error.response?.data?.detail || "Fehler bei der Zusage");
    }
  };

  const openCancelDialog = (assignment) => {
    if (!assignment?.id) return;
    setAssignmentToCancel(assignment);
    setCancelDialogOpen(true);
  };

  const handleCancelAssignment = async () => {
    if (!assignmentToCancel?.id) return;
    setCancelingAssignmentId(assignmentToCancel.id);

    try {
      await axios.delete(`${API_URL}/api/assignments/${assignmentToCancel.id}`);
      toast.success("Zusage zurückgenommen");
      setCancelDialogOpen(false);
      setAssignmentToCancel(null);

      // Refresh data
      const [assignRes, shiftsRes] = await Promise.all([
        axios.get(`${API_URL}/api/my-assignments`),
        axios.get(`${API_URL}/api/shifts/${dienstplan.id}`)
      ]);
      setMyAssignments(assignRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      console.error("Error canceling assignment:", error);
      toast.error("Fehler beim Zurücknehmen der Zusage");
    } finally {
      setCancelingAssignmentId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  const handlePlanChange = async (planId) => {
    if (!planId || String(planId) === String(dienstplan?.id)) return;
    const selectedPlan = dienstplaene.find((plan) => String(plan.id) === String(planId));
    if (!selectedPlan) return;

    localStorage.setItem(MEMBER_SELECTED_PLAN_KEY, String(selectedPlan.id));

    if (canManagePlan(selectedPlan)) {
      navigate("/dashboard");
      return;
    }

    setSwitchingPlan(true);
    try {
      setDienstplan(selectedPlan);
      const [shiftsRes, assignRes] = await Promise.all([
        axios.get(`${API_URL}/api/shifts/${selectedPlan.id}`),
        axios.get(`${API_URL}/api/my-assignments`),
        loadPlanSettings(selectedPlan.id),
      ]);
      setShifts(shiftsRes.data || []);
      setMyAssignments(assignRes.data || []);
      setCommentDrafts({});
    } catch (error) {
      console.error("Error changing dienstplan:", error);
      toast.error("Fehler beim Wechseln des Dienstplans");
    } finally {
      setSwitchingPlan(false);
    }
  };

  const isAssigned = (shiftId) => {
    return myAssignments.some(a => a.shift_id === shiftId);
  };

  const getAssignment = (shiftId) => {
    return myAssignments.find(a => a.shift_id === shiftId);
  };

  const getCommentDraft = (assignment) => {
    if (!assignment) return "";
    if (Object.prototype.hasOwnProperty.call(commentDrafts, assignment.id)) {
      return commentDrafts[assignment.id];
    }
    return assignment.comment || "";
  };

  const handleSaveComment = async (assignment) => {
    if (!assignment?.id) return;
    const comment = (getCommentDraft(assignment) || "").trim();
    setSavingCommentId(assignment.id);
    try {
      const response = await axios.put(`${API_URL}/api/assignments/${assignment.id}/comment`, { comment });
      setMyAssignments((prev) =>
        prev.map((item) => (item.id === assignment.id ? { ...item, comment: response.data?.comment || "" } : item)),
      );
      setCommentDrafts((prev) => {
        const next = { ...prev };
        delete next[assignment.id];
        return next;
      });
      toast.success("Kommentar gespeichert");
    } catch (error) {
      console.error("Error saving assignment comment:", error);
      toast.error(error.response?.data?.detail || "Kommentar konnte nicht gespeichert werden");
    } finally {
      setSavingCommentId(null);
    }
  };

  const getShiftStatus = (shift) => {
    if (shift.confirmed_count >= shift.required_staff) return "filled";
    if (shift.confirmed_count > 0) return "partial";
    return "open";
  };

  const viewAssignments = async (shift) => {
    setSelectedShift(shift);
    setAssignmentsOpen(true);
    setLoadingAssignments(true);

    try {
      const response = await axios.get(`${API_URL}/api/assignments/${shift.id}`);
      setAssignments(response.data || []);
    } catch (error) {
      console.error("Error loading assignments for member:", error);
      toast.error("Fehler beim Laden der Zusagen");
    } finally {
      setLoadingAssignments(false);
    }
  };

  const visibleFromDate = subDays(startOfDay(new Date()), 7);
  const visibleShifts = shifts.filter((shift) => {
    const shiftDate = parseISO(shift.date);
    return !Number.isNaN(shiftDate.getTime()) && shiftDate >= visibleFromDate;
  });
  const sortedVisibleShifts = [...visibleShifts].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    const aStart = (a.start_time || "23:59").toString();
    const bStart = (b.start_time || "23:59").toString();
    return aStart.localeCompare(bStart);
  });
  const dienstplanImageUrl = dienstplan?.image_url
    ? (String(dienstplan.image_url).startsWith("http") ? dienstplan.image_url : `${API_URL}${dienstplan.image_url}`)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#17c671]" />
        </div>
        <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
      </div>
    );
  }

  if (!dienstplan) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="no-dienstplan">
        <AppHeader
          homePath="/member"
          actions={<UserMenuButton user={user} onLogout={handleLogout} />}
        />

        <div className="flex-1 flex items-center justify-center p-6 pt-20">
          <Card className="max-w-md w-full border-gray-200 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CalendarDays className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-[#212529] mb-2">
                Kein Dienstplan
              </h2>
              <p className="text-gray-600 mb-6">
                Du bist noch keinem Dienstplan zugeordnet. Bitte verwende einen Einladungs-Link.
              </p>
              <Button onClick={handleLogout} variant="outline" className="border-gray-300">
                <LogOut className="w-4 h-4 mr-2" />
                Abmelden
              </Button>
            </CardContent>
          </Card>
        </div>
        <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="member-dashboard-page">
      <AppHeader
        homePath="/member"
        center={dienstplaene.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white">
                <span className="text-gray-300 mr-1">Dienstplan:</span>
                <span className="max-w-[190px] truncate">{dienstplan?.name || "Nicht ausgewählt"}</span>
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              {dienstplaene.map((plan) => (
                <DropdownMenuItem
                  key={plan.id}
                  onClick={() => handlePlanChange(plan.id)}
                  className={`cursor-pointer ${String(dienstplan?.id) === String(plan.id) ? "font-semibold text-[#17c671]" : ""}`}
                >
                  {plan.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        actions={
          <>
            {dienstplaene.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="md:hidden h-9 w-9 rounded-full p-0 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                    aria-label="Dienstplan-Menü öffnen"
                  >
                    <Menu className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuItem className="font-semibold text-gray-500 cursor-default">
                    Dienstplan wählen
                  </DropdownMenuItem>
                  {dienstplaene.map((plan) => (
                    <DropdownMenuItem
                      key={plan.id}
                      onClick={() => handlePlanChange(plan.id)}
                      className={`cursor-pointer ${String(dienstplan?.id) === String(plan.id) ? "font-semibold text-[#17c671]" : ""}`}
                    >
                      {plan.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            <UserMenuButton user={user} onLogout={handleLogout} />
          </>
        }
      />
      
      {/* Main Content - Light */}
      <main className="max-w-5xl mx-auto px-6 py-8 flex-1 w-full">
        {switchingPlan ? (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 flex items-center justify-center shadow-sm">
            <Loader2 className="w-5 h-5 animate-spin text-[#17c671]" />
            <span className="ml-2 text-sm text-gray-600">Dienstplan wird geladen...</span>
          </div>
        ) : null}

        {/* Dienstplan Info */}
        <Card className="mb-8 animate-fade-in border-gray-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
              <div className="flex-1 min-w-0 h-[50px] flex items-center">
                <h1 className="font-heading text-2xl font-semibold text-[#212529] leading-tight truncate">
                  {dienstplan.name}
                </h1>
              </div>
              <div className="w-full md:ml-auto md:max-w-[220px]">
                {dienstplanImageUrl ? (
                  <img
                    src={dienstplanImageUrl}
                    alt={dienstplan.name}
                    className="h-[50px] w-full object-contain rounded-lg border border-gray-200"
                  />
                ) : (
                  <div className="h-[50px] w-full bg-[#17c671]/10 rounded-lg flex items-center justify-center border border-gray-200">
                    <CalendarDays className="w-6 h-6 text-[#17c671]" />
                  </div>
                )}
              </div>
            </div>
            {dienstplan.description && (
              <CardDescription className="mt-3 text-gray-500">{dienstplan.description}</CardDescription>
            )}
            {(planSettings.user_can_see_other_users && (
              dienstplan.responsible_name ||
              (planSettings.user_can_see_mydetails_mail && dienstplan.responsible_email) ||
              (planSettings.user_can_see_mydetails_phone && dienstplan.responsible_phone)
            )) && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="text-sm font-medium text-[#212529]">
                  Ersteller: {dienstplan.responsible_name || "-"}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  {planSettings.user_can_see_mydetails_mail && dienstplan.responsible_email ? (
                    <a
                      href={`mailto:${dienstplan.responsible_email}`}
                      className="inline-flex items-center gap-1 text-[#17c671] hover:underline"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {dienstplan.responsible_email}
                    </a>
                  ) : null}
                  {planSettings.user_can_see_mydetails_phone && dienstplan.responsible_phone ? (
                    <a
                      href={`tel:${dienstplan.responsible_phone}`}
                      className="inline-flex items-center gap-1 text-[#17c671] hover:underline"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {dienstplan.responsible_phone}
                    </a>
                  ) : null}
                </div>
              </div>
            )}
          </CardHeader>
        </Card>

        {planSettings.user_see_schedule_comment && planSettings.schedule_comment ? (
          <Card className="mb-8 animate-fade-in border-amber-200 bg-amber-50/60 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base text-amber-800">Hinweistext</CardTitle>
              <CardDescription className="text-amber-900 whitespace-pre-wrap">
                {planSettings.schedule_comment}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : null}
        
        {/* My Assignments Summary */}
        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <Card className="animate-fade-in stagger-1 border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-[#17c671]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{myAssignments.length}</p>
                  <p className="text-sm text-gray-500">Meine Zusagen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="animate-fade-in stagger-2 border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{visibleShifts.length}</p>
                  <p className="text-sm text-gray-500">Verfügbare Schichten</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Shifts List */}
        <h2 className="font-heading text-xl font-semibold text-[#212529] mb-4">Verfügbare Schichten</h2>
        
        {sortedVisibleShifts.length === 0 ? (
          <Card className="animate-fade-in border-gray-200">
            <CardContent className="py-12 text-center text-gray-500">
              <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Noch keine Schichten vorhanden</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full min-w-[920px]" data-testid="member-shifts-table">
                <thead>
                  <tr className="bg-[#f7f8fa] border-b border-gray-200">
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">Tag</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#111827]">Veranstaltung</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#111827]">Details</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#111827]">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVisibleShifts.map((shift) => {
                    const assigned = isAssigned(shift.id);
                    const assignment = getAssignment(shift.id);
                    const status = getShiftStatus(shift);
                    const isFilled = status === "filled" && !assigned;
                    const parsedDate = parseISO(shift.date);
                    const weekday = format(parsedDate, "EEE", { locale: de }).replace(".", "");
                    const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
                    const dayLabel = format(parsedDate, "d", { locale: de });
                    const month = format(parsedDate, "MMM", { locale: de }).replace(".", "");
                    const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);

                    return (
                      <tr key={shift.id} className="border-t border-gray-200" data-testid={`member-shift-${shift.id}`}>
                        <td className="px-4 py-4 text-center align-middle">
                          <div className="inline-flex flex-col items-center leading-none gap-0.5">
                            <span className="text-xs text-gray-500 mb-0.5">{weekdayLabel}</span>
                            <span className="text-3xl font-bold text-[#111827]">{dayLabel}</span>
                            <span className="text-base text-gray-500 mt-0.5">{monthLabel}</span>
                            {(shift.start_time || shift.end_time) && (
                              <span className="mt-2 text-sm font-medium text-slate-500">
                                {`${shift.start_time || "--:--"} - ${shift.end_time || "--:--"}`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <p className="font-medium text-[#212529]">{shift.name}</p>
                          {shift.description ? <p className="text-sm text-gray-500 mt-1">{shift.description}</p> : null}
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <div className="space-y-1 text-sm text-gray-600">
                            <p
                              className={planSettings.user_can_see_other_users ? "cursor-pointer text-amber-600 hover:text-amber-700 font-medium" : ""}
                              onClick={planSettings.user_can_see_other_users ? () => viewAssignments(shift) : undefined}
                              data-testid={planSettings.user_can_see_other_users ? `member-view-assignments-${shift.id}` : undefined}
                            >
                              {planSettings.user_can_see_users_needed
                                ? `${shift.confirmed_count}/${shift.required_staff} Zusagen`
                                : `${shift.confirmed_count} Zusagen`}
                            </p>
                            {shift.position ? <p>{shift.position}</p> : null}
                            {shift.location ? (
                              <p className="inline-flex items-center gap-1 text-[#17c671] font-medium">
                                <MapPin className="w-3 h-3" />
                                {shift.location}
                              </p>
                            ) : null}
                            {(shift.start_time || shift.end_time) ? (
                              <p className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {shift.start_time || "--:--"}{shift.end_time ? ` - ${shift.end_time}` : ""}
                              </p>
                            ) : null}
                            {planSettings.user_can_comment_days ? (
                              assigned ? (
                                <div className="mt-2 space-y-2">
                                  <Textarea
                                    rows={2}
                                    value={getCommentDraft(assignment)}
                                    onChange={(e) =>
                                      setCommentDrafts((prev) => ({ ...prev, [assignment.id]: e.target.value }))
                                    }
                                    className="border-gray-300 focus:border-[#17c671] text-sm"
                                    placeholder="Kommentar zu deinem Tag..."
                                    data-testid={`member-comment-input-${shift.id}`}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="h-8 rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white text-xs font-medium"
                                    onClick={() => handleSaveComment(assignment)}
                                    disabled={savingCommentId === assignment.id}
                                    data-testid={`member-comment-save-${shift.id}`}
                                  >
                                    {savingCommentId === assignment.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                    Speichern
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 mt-2">Kommentar nach Zusage möglich.</p>
                              )
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center align-middle">
                          {assigned ? (
                            <Badge className="bg-[#17c671]/10 text-[#17c671] border-[#17c671]/30">Zugesagt</Badge>
                          ) : isFilled ? (
                            <Badge className="bg-gray-100 text-gray-600 border-gray-200">Voll besetzt</Badge>
                          ) : (
                            <Badge className="bg-amber-50 text-amber-600 border-amber-200">Offen</Badge>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center align-middle">
                          <div className="flex flex-col items-center gap-2">
                          {assigned ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 min-w-[120px] rounded-full border-2 border-rose-300 text-rose-500 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-600 transition-colors text-sm font-medium"
                              onClick={() => openCancelDialog(assignment)}
                              data-testid={`cancel-assignment-${shift.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Absagen
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="h-9 min-w-[120px] rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white text-sm font-medium"
                              onClick={() => handleConfirmShift(shift.id)}
                              data-testid={`confirm-shift-${shift.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Zusagen
                            </Button>
                          )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {sortedVisibleShifts.map((shift) => {
                const assigned = isAssigned(shift.id);
                const assignment = getAssignment(shift.id);
                const parsedDate = parseISO(shift.date);
                const weekday = format(parsedDate, "EEE", { locale: de }).replace(".", "");
                const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
                const dayLabel = format(parsedDate, "d", { locale: de });
                const month = format(parsedDate, "MMM", { locale: de }).replace(".", "");
                const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);
                const timeLabel = shift.start_time || shift.end_time
                  ? `${shift.start_time || "--:--"} - ${shift.end_time || "--:--"}`
                  : null;

                return (
                  <div
                    key={shift.id}
                    className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm"
                    data-testid={`member-shift-mobile-${shift.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="min-w-[56px] text-center leading-none">
                        <p className="text-[10px] text-gray-500">{weekdayLabel}</p>
                        <p className="text-xl font-bold text-[#111827] mt-0.5">{dayLabel}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{monthLabel}</p>
                      </div>
                      {timeLabel ? (
                        <span className="text-xs text-slate-500 whitespace-nowrap">{timeLabel}</span>
                      ) : null}
                      <div className="min-w-0 flex flex-col">
                        <span className="text-sm font-medium text-[#212529] truncate">{shift.name}</span>
                        <span
                          className={`text-xs whitespace-nowrap ${planSettings.user_can_see_other_users ? "cursor-pointer text-amber-600 hover:text-amber-700 font-medium" : "text-gray-500"}`}
                          onClick={planSettings.user_can_see_other_users ? () => viewAssignments(shift) : undefined}
                          data-testid={planSettings.user_can_see_other_users ? `member-view-assignments-mobile-${shift.id}` : undefined}
                        >
                          {planSettings.user_can_see_users_needed
                            ? `${shift.confirmed_count}/${shift.required_staff} Zusagen`
                            : `${shift.confirmed_count} Zusagen`}
                        </span>
                      </div>
                      <div className="ml-auto">
                      {assigned ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-full border-2 border-rose-300 text-rose-500 hover:bg-rose-50 hover:border-rose-400 hover:text-rose-600 transition-colors text-xs font-medium px-3"
                          onClick={() => openCancelDialog(assignment)}
                          data-testid={`cancel-assignment-mobile-${shift.id}`}
                          aria-label="Absagen"
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Absagen
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="h-8 rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white text-xs font-medium px-3"
                          onClick={() => handleConfirmShift(shift.id)}
                          data-testid={`confirm-shift-mobile-${shift.id}`}
                          aria-label="Zusagen"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Zusagen
                        </Button>
                      )}
                      </div>
                    </div>
                    {planSettings.user_can_comment_days ? (
                      assigned ? (
                        <div className="mt-2 space-y-2">
                          <Textarea
                            rows={2}
                            value={getCommentDraft(assignment)}
                            onChange={(e) =>
                              setCommentDrafts((prev) => ({ ...prev, [assignment.id]: e.target.value }))
                            }
                            className="border-gray-300 focus:border-[#17c671] text-sm"
                            placeholder="Kommentar zu deinem Tag..."
                            data-testid={`member-comment-mobile-input-${shift.id}`}
                          />
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 rounded-full bg-[#17c671] hover:bg-[#14a85d] text-white text-xs font-medium"
                            onClick={() => handleSaveComment(assignment)}
                            disabled={savingCommentId === assignment.id}
                            data-testid={`member-comment-mobile-save-${shift.id}`}
                          >
                            {savingCommentId === assignment.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                            Speichern
                          </Button>
                        </div>
                      ) : (
                        <p className="mt-3 text-xs text-gray-400">Kommentar nach Zusage möglich.</p>
                      )
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          if (!open && cancelingAssignmentId) return;
          setCancelDialogOpen(open);
          if (!open) setAssignmentToCancel(null);
        }}
      >
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#212529]">Zusage wirklich absagen?</DialogTitle>
            <DialogDescription className="text-gray-500">
              Deine Zusage wird entfernt. Du kannst danach erneut zusagen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-gray-300"
              onClick={() => {
                if (cancelingAssignmentId) return;
                setCancelDialogOpen(false);
                setAssignmentToCancel(null);
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCancelAssignment}
              disabled={Boolean(cancelingAssignmentId)}
              data-testid="confirm-cancel-assignment-btn"
            >
              {cancelingAssignmentId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Absagen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignmentsOpen} onOpenChange={setAssignmentsOpen}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#212529]">Zusagen für {selectedShift?.name}</DialogTitle>
            <DialogDescription className="text-gray-500">
              {selectedShift?.date ? format(parseISO(selectedShift.date), "d. MMMM yyyy", { locale: de }) : ""}
              {(selectedShift?.start_time || selectedShift?.event_start) ? ` - ${selectedShift?.start_time || selectedShift?.event_start}` : ""}
              {(selectedShift?.end_time || selectedShift?.event_end) ? ` - ${selectedShift?.end_time || selectedShift?.event_end}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">
                {planSettings.user_can_see_users_needed
                  ? `${assignments.length} von ${selectedShift?.required_staff} benötigten Mitarbeitern`
                  : `${assignments.length} Zusagen`}
              </span>
              <Badge className={getShiftStatus(selectedShift || {}) === "filled"
                ? "bg-[#17c671]/10 text-[#17c671] border-[#17c671]/30"
                : (getShiftStatus(selectedShift || {}) === "partial"
                  ? "bg-amber-50 text-amber-600 border-amber-200"
                  : "bg-red-50 text-red-600 border-red-200")}
              >
                {getShiftStatus(selectedShift || {}) === "filled"
                  ? "Besetzt"
                  : (getShiftStatus(selectedShift || {}) === "partial" ? "Teilweise besetzt" : "Offen")}
              </Badge>
            </div>

            {loadingAssignments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#17c671]" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>Noch keine Zusagen</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assignments.map((assignment) => (
                  <div key={assignment.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{assignment.user_name}</p>
                      <p className="text-sm text-slate-500">{assignment.user_email}</p>
                    </div>
                  </div>
                    {assignment.comment?.trim() ? (
                      <div className="mt-2 rounded-md border border-green-100 bg-white px-3 py-2">
                        <p className="text-xs font-medium text-gray-500">Kommentar</p>
                        <p className="text-sm text-slate-700 break-words">{assignment.comment}</p>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
    </div>
  );
};

export default MemberDashboardPage;

